import tinycolor from 'tinycolor2'
import BRAND_SHADES from './brand-shades.json'

/**
 * Images in the accent. Stylesheets follow the accent through variables
 * (postcss-theme-colours.js), but a picture cannot read them, so with any
 * accent but the default the purple in pictures is changed here, at runtime.
 * With the default accent nothing below runs and every image is as drawn.
 *
 * - Icons (`<img>` of our own SVGs: the challenge trophy, the leaderboard
 *   ranks, …): each SVG holding a brand colour is fetched once, its brand
 *   colours swapped for the accent's (the same colour -> variable list the
 *   build uses), and shown as a data URI. An observer catches images added or
 *   changed later; the original src comes back with the default accent. The
 *   CoreDirection logo, mark and frame keep their colours. Same-origin
 *   iframes are watched too (MoEngage's notification inbox).
 *
 * - Raster art inside those SVGs (the "No records found" illustration is a
 *   PNG wrapped in an SVG): its purple pixels move the way presets.js moves a
 *   brand shade, so #690FAD becomes the accent and its tints stay tints.
 *
 * - The 404 illustration is Vuexy's, drawn in Vuexy's violet (#7367F0) rather
 *   than ours: its violets move onto the accent's hue.
 *
 * - Default avatars. A member without a photo has a copy of a default avatar
 *   (purple shirt, purple ring) on the CDN, named …-image-user-avatar-0N. The
 *   CDN sends no CORS headers, so the picture cannot be read, and copies of
 *   the same name do not always hold the same picture. An SVG colour matrix
 *   (CSS `filter`) only paints, so it works on any of them: it is solved so
 *   that #690FAD becomes the accent exactly while white, black and the
 *   avatars' lavender skin stay as they are; the dark hair and the off-white
 *   background move by a step or two. Being linear, it keeps the soft edges
 *   between colours right.
 *
 * - Other brand-purple pictures from the CDN, such as the challenge type icons
 *   (purple glyphs the backend serves as PNGs), get the same filter. Mark
 *   their <img> with the class `cd-accent-art`. Only use it for pictures
 *   drawn in the brand purple, since every other colour in them shifts too.
 */

const DEFAULT_PRIMARY = '#690fad'

// The same colour -> variable names as the build (postcss-theme-colours.js).
const TOKENS = {
  '690fad': '--primary-default',
  caa8f5: '--secondary-default',
  '6d28d9': '--cd-gradient-start',
  b5179e: '--cd-gradient-mid',
  f15b8f: '--cd-gradient-end',
}
BRAND_SHADES.forEach(hex => { TOKENS[hex] = `--cd-shade-${hex}` })

const KEEP = /logo|coreicon|coredirection-frame/i
const ILLUSTRATION = /\/error(-dark)?\.(?:[0-9a-f]+\.)?svg/i
// Vuexy's primary, the violet the illustration is drawn around.
const ILLUSTRATION_PRIMARY = '#7367f0'
// The CDN copies only (…-image-user-avatar-0N): the app's own avatar files are
// other drawings, and only shown on the sign-up pages, which keep the brand.
const DEFAULT_AVATAR = '-image-user-avatar-0'
// Brand-purple pictures the filter is for: <img class="cd-accent-art">.
const ART_CLASS = 'cd-accent-art'
// The avatars' skin, which the filter keeps exactly.
const AVATAR_SKIN = '#d0cde1'
const ORIGINAL_SRC = 'data-cd-accent-src'
const FILTER_ID = 'cd-accent-filter'

const fullHex = digits => (digits.length === 3 ? digits.split('').map(c => c + c).join('') : digits).toLowerCase()
// A colour's channels, 0–1.
const unit = hex => {
  const { r, g, b } = tinycolor(hex).toRgb()
  return [r / 255, g / 255, b / 255]
}

// ---------------------------------------------------------------------------
// SVG icons
// ---------------------------------------------------------------------------

let colours = null // hex (no #) -> the accent's colour for it
let accentPrimary = null
// Bumped whenever the accent changes, so work started for the old one is dropped.
let generation = 0
const sources = new Map() // url -> Promise<svg text | null>
const pending = new Map() // src -> Promise<data URI | null>
const recoloured = new Map() // src -> data URI, or null for "nothing to change"

const fetchSvg = url => {
  if (!sources.has(url)) {
    sources.set(url, fetch(url).then(r => (r.ok ? r.text() : null)).catch(() => null))
  }
  return sources.get(url)
}

/**
 * One of the illustration's violets on the accent's hue: Vuexy's primary
 * becomes the accent, and a tint or shade of it keeps its distance from it
 * (presets.js shiftShade, with the hue set rather than turned).
 */
function onAccentHue(hex, primary) {
  const s = tinycolor(hex).toHsl()
  const f = tinycolor(ILLUSTRATION_PRIMARY).toHsl()
  const t = tinycolor(primary).toHsl()
  const weight = s.l > f.l ? (1 - s.l) / (1 - f.l) : s.l / f.l
  return tinycolor({
    h: t.h,
    s: Math.min(1, s.s * (t.s / f.s)),
    l: Math.min(1, Math.max(0, s.l + (t.l - f.l) * weight)),
  }).toHexString()
}

const isViolet = hex => {
  const { h, s } = tinycolor(`#${hex}`).toHsl()
  return s >= 0.6 && h >= 238 && h <= 300
}

// RGB (0–1) <-> HSL (h in degrees), inline for speed: an illustration has
// hundreds of thousands of pixels.
function toHsl(r, g, b) {
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  if (max === min) return [0, 0, l]
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) * 60
  else if (max === g) h = ((b - r) / d + 2) * 60
  else h = ((r - g) / d + 4) * 60
  return [h, s, l]
}

function toRgb(h, s, l) {
  const k = n => (n + h / 30) % 12
  const a = s * Math.min(l, 1 - l)
  const f = n => l - a * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1))
  return [f(0), f(8), f(4)].map(c => Math.round(c * 255))
}

/**
 * Where one pixel of brand-purple art goes for the accent `to`, or null to
 * leave it: the purple ones move as presets.js shiftShade moves a brand shade
 * (hue turned with the primary, saturation scaled, lightness following the
 * primary near it), so #690FAD lands on the accent and a pale tint stays pale.
 */
function shiftedPixel(r, g, b, from, to) {
  const [h, s, l] = toHsl(r / 255, g / 255, b / 255)
  if (!(s > 0.25 && h >= 255 && h <= 335 && l > 0.05 && l < 0.97)) return null
  const weight = l > from[2] ? (1 - l) / (1 - from[2]) : l / from[2]
  return toRgb(
    (h + to[0] - from[0] + 360) % 360,
    Math.min(1, s * (to[1] / from[1])),
    Math.min(1, Math.max(0, l + (to[2] - from[2]) * weight)),
  )
}

/** A raster image (data URI) with its purple turned to the accent; null if it has none. */
function recolourRaster(uri) {
  const from = toHsl(...unit(DEFAULT_PRIMARY))
  const to = toHsl(...unit(accentPrimary))
  return new Promise(resolve => {
    const image = new Image()
    image.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = image.naturalWidth
      canvas.height = image.naturalHeight
      const context = canvas.getContext('2d')
      context.drawImage(image, 0, 0)
      const pixels = context.getImageData(0, 0, canvas.width, canvas.height)
      const { data } = pixels
      // Art has few distinct colours: work each out once.
      const seen = new Map()
      let changed = false
      for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] !== 0) {
          const key = data[i] * 65536 + data[i + 1] * 256 + data[i + 2]
          if (!seen.has(key)) seen.set(key, shiftedPixel(data[i], data[i + 1], data[i + 2], from, to))
          const rgb = seen.get(key)
          if (rgb) {
            [data[i], data[i + 1], data[i + 2]] = rgb
            changed = true
          }
        }
      }
      if (!changed) {
        resolve(null)
        return
      }
      context.putImageData(pixels, 0, 0)
      resolve(canvas.toDataURL('image/png'))
    }
    image.onerror = () => resolve(null)
    image.src = uri
  })
}

const EMBEDDED_RASTER = /data:image\/(?:png|jpeg|webp);base64,[A-Za-z0-9+/=\s]+/g

async function recolourSvg(text, src) {
  if (ILLUSTRATION.test(src)) {
    return text.replace(/#([0-9a-fA-F]{6})\b/g, (match, digits) => (isViolet(digits) ? onAccentHue(`#${digits}`, accentPrimary) : match))
  }
  let result = text.replace(/#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})\b/g, (match, digits) => colours[fullHex(digits)] || match)
  const rasters = [...new Set(result.match(EMBEDDED_RASTER) || [])]
  const replacements = await Promise.all(rasters.map(recolourRaster))
  rasters.forEach((uri, i) => {
    if (replacements[i]) result = result.split(uri).join(replacements[i])
  })
  return result
}

function show(img, src, dataUri) {
  if (!dataUri || !colours || img.getAttribute('src') !== src) return
  img.setAttribute(ORIGINAL_SRC, src)
  img.setAttribute('src', dataUri)
}

function recolourImage(img) {
  const src = img.getAttribute('src') || ''
  if (!colours || !src || src.startsWith('data:') || KEEP.test(src)) return
  if (!/\.svg(\?|#|$)/i.test(src)) return
  const url = new URL(src, window.location.href)
  if (url.origin !== window.location.origin) return
  // Seen before: swap at once, so a hover icon does not flash purple.
  if (recoloured.has(src)) {
    show(img, src, recoloured.get(src))
    return
  }
  if (!pending.has(src)) {
    const run = generation
    pending.set(src, fetchSvg(url.href)
      .then(text => (text && run === generation ? recolourSvg(text, src).then(result => (result === text ? null : result)) : null))
      .then(result => {
        if (run !== generation) return null
        const dataUri = result && `data:image/svg+xml;charset=utf-8,${encodeURIComponent(result)}`
        recoloured.set(src, dataUri)
        return dataUri
      }))
  }
  pending.get(src).then(dataUri => show(img, src, dataUri))
}

// Same-origin iframes are watched like the page: MoEngage draws its inbox,
// "no notifications" picture included, in a srcdoc iframe, and rewrites that
// document whenever the inbox opens.
let observers = [] // one per document being watched
let frames = [] // [iframe, its load listener]
const watchedFrames = new WeakSet()

function recolourImagesIn(node) {
  if (node.nodeType !== 1) return
  if (node.tagName === 'IMG') recolourImage(node)
  // eslint-disable-next-line no-use-before-define
  else if (node.tagName === 'IFRAME') watchFrame(node)
  else {
    node.querySelectorAll('img').forEach(recolourImage)
    // eslint-disable-next-line no-use-before-define
    node.querySelectorAll('iframe').forEach(watchFrame)
  }
}

function watchDocument(doc) {
  if (!doc || !doc.body) return
  recolourImagesIn(doc.body)
  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      if (mutation.type === 'attributes') {
        // Vue set a new src over the recoloured one: recolour that instead.
        const img = mutation.target
        if (!(img.getAttribute('src') || '').startsWith('data:')) img.removeAttribute(ORIGINAL_SRC)
        recolourImage(img)
      } else {
        mutation.addedNodes.forEach(recolourImagesIn)
      }
    })
  })
  observer.observe(doc.body, {
    subtree: true, childList: true, attributes: true, attributeFilter: ['src'],
  })
  observers.push(observer)
}

// A cross-origin frame's document is null (or throws): it is left alone.
const frameDocument = frame => {
  try {
    return frame.contentDocument
  } catch (e) {
    return null
  }
}

function watchFrame(frame) {
  if (watchedFrames.has(frame)) return
  watchedFrames.add(frame)
  // Each load is a new document (MoEngage swaps the srcdoc).
  const onLoad = () => watchDocument(frameDocument(frame))
  frame.addEventListener('load', onLoad)
  frames.push([frame, onLoad])
  const doc = frameDocument(frame)
  if (doc && doc.readyState === 'complete') watchDocument(doc)
}

function watchImages() {
  watchDocument(document)
}

function restoreImages() {
  observers.forEach(observer => observer.disconnect())
  observers = []
  frames.forEach(([frame, onLoad]) => {
    frame.removeEventListener('load', onLoad)
    watchedFrames.delete(frame)
  })
  const documents = [document, ...frames.map(([frame]) => frameDocument(frame)).filter(Boolean)]
  frames = []
  colours = null
  accentPrimary = null
  generation += 1
  pending.clear()
  recoloured.clear()
  documents.forEach(doc => doc.querySelectorAll(`img[${ORIGINAL_SRC}]`).forEach(img => {
    img.setAttribute('src', img.getAttribute(ORIGINAL_SRC))
    img.removeAttribute(ORIGINAL_SRC)
  }))
}

// ---------------------------------------------------------------------------
// Default avatars and other brand-purple pictures from the CDN
// ---------------------------------------------------------------------------

const dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2]
const cross = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]

/**
 * The 4x5 feColorMatrix taking #690FAD to `primary`: M = I + d·eᵀ, where d is
 * how far the colour moves and e is zero on every grey and on the skin, and 1
 * on #690FAD. A grey or the skin is left alone, #690FAD lands on `primary`,
 * and a mix of them lands on the same mix of where they go.
 */
function artMatrix(primary) {
  const from = unit(DEFAULT_PRIMARY)
  const d = unit(primary).map((c, i) => c - from[i])
  const normal = cross([1, 1, 1], unit(AVATAR_SKIN))
  const e = normal.map(c => c / dot(normal, from))
  const rows = [0, 1, 2].map(i => [0, 1, 2].map(j => (i === j ? 1 : 0) + d[i] * e[j]).concat([0, 0]))
  rows.push([0, 0, 0, 1, 0])
  return rows.map(row => row.map(n => +n.toFixed(5)).join(' ')).join(' ')
}

function showArtFilter(primary) {
  let holder = document.getElementById(FILTER_ID)
  if (!holder) {
    holder = document.createElement('div')
    holder.id = FILTER_ID
    holder.setAttribute('aria-hidden', 'true')
    holder.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden'
    holder.innerHTML = `<svg width="0" height="0" focusable="false"><filter id="${FILTER_ID}-filter" color-interpolation-filters="sRGB"><feColorMatrix type="matrix"/></filter></svg>`
      + `<style>img[src*="${DEFAULT_AVATAR}"],img.${ART_CLASS}{filter:url(#${FILTER_ID}-filter)}</style>`
    document.body.appendChild(holder)
  }
  holder.querySelector('feColorMatrix').setAttribute('values', artMatrix(primary))
}

function hideArtFilter() {
  const holder = document.getElementById(FILTER_ID)
  if (holder) holder.remove()
}

// ---------------------------------------------------------------------------

let shown = null

/**
 * Brings the page's pictures in line with the accent: `vars` are the accent's
 * custom properties (presets.js accentVariables), or null for the default
 * accent, which puts every picture back as drawn. `key` names the accent and
 * mode, so applying the same theme again leaves the pictures alone.
 */
export default function showAccentImages(key, vars) {
  if (key === shown) return
  shown = key
  restoreImages()
  if (!vars) {
    hideArtFilter()
    return
  }
  accentPrimary = vars['--primary-default']
  colours = {}
  Object.entries(TOKENS).forEach(([hex, token]) => {
    if (vars[token]) colours[hex] = vars[token]
  })
  showArtFilter(accentPrimary)
  watchImages()
}
